import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const sections = [
  {
    id: 'grounding',
    emoji: '🫂',
    title: 'Start from where you are',
    description: 'Living with HIV does not cancel your joy, sex life, dating, or right to party carefully. You deserve support that feels human, not clinical.',
    bullets: [
      'You are not “less than” because of your status.',
      'You deserve partners, friends, and care teams who respect you.',
      'Peer support can matter just as much as medical support.'
    ]
  },
  {
    id: 'treatment',
    emoji: '💊',
    title: 'Meds + routines that actually stick',
    description: 'Treatment works best when it fits real life. If partying, travel, or comedowns throw you off, build a routine that bends instead of breaks.',
    bullets: [
      'Set one quiet phone reminder that does not out you.',
      'Keep a backup dose in your bag or jacket pocket when safe to do so.',
      'After a long session, take meds as soon as you realize you missed your usual time.',
      'If missed doses happen often, talk to your clinic about simpler options.'
    ]
  },
  {
    id: 'u-equals-u',
    emoji: '💙',
    title: 'U=U is real',
    description: 'Undetectable = Untransmittable. When your viral load stays undetectable, you do not pass HIV through sex. That is powerful, loving, and backed by science.',
    bullets: [
      'U=U does not mean “maybe” — it means effectively no sexual transmission risk.',
      'Taking meds consistently protects your health and your partners.',
      'Regular labs help you know where things stand without guessing.'
    ]
  },
  {
    id: 'partying',
    emoji: '✨',
    title: 'Partying while taking care of yourself',
    description: 'Pleasure and protection can live together. A little planning can reduce chaos before, during, and after a session.',
    bullets: [
      'Pack meds, water, lube, condoms, and anything that supports safer use.',
      'Use alarms for meds, hydration, and a check-in with yourself.',
      'Plan recovery time for sleep, food, and emotional reset afterward.',
      'If a substance or hookup dynamic makes meds harder, that is useful information — not failure.'
    ]
  },
  {
    id: 'red-flags',
    emoji: '🚨',
    title: 'When to reach out fast',
    description: 'Some moments need extra support. Trust your body and your gut if something feels off.',
    bullets: [
      'Chest pain, severe dehydration, trouble breathing, or confusion need urgent care.',
      'If your mood crashes hard after partying, reach out to a peer, hotline, or clinic.',
      'If meds are making you feel rough or you are worried about interactions, talk to a pharmacist or HIV clinic.'
    ]
  }
];

const resources = [
  {
    id: 'catie',
    name: 'CATIE',
    description: 'Canadian HIV and hepatitis C education, treatment info, and practical guides.',
    href: 'https://www.catie.ca/'
  },
  {
    id: 'aco',
    name: 'AIDS Committee of Ottawa',
    description: 'Local Ottawa HIV support, education, harm reduction, and community programs.',
    href: 'https://aco-cso.ca/'
  },
  {
    id: 'casey-house',
    name: 'Casey House (Toronto)',
    description: 'Specialized hospital and community-based care for people living with HIV.',
    href: 'https://www.caseyhouse.com/'
  },
  {
    id: 'halco',
    name: 'HIV & AIDS Legal Clinic (HALCO)',
    description: 'Legal aid, advocacy, and rights information for people living with or affected by HIV.',
    href: 'https://www.halco.org/'
  },
  {
    id: 'cbrc',
    name: 'CBRC',
    description: 'Queer and trans health research, education, and community resources across Canada.',
    href: 'https://www.cbrc.net/'
  },
  {
    id: 'getakit',
    name: 'GetaKit',
    description: 'Order free HIV self-test kits in participating Canadian regions.',
    href: 'https://getakit.ca/'
  },
  {
    id: 'hiv411',
    name: 'HIV411',
    description: 'Find HIV and sexual health services across Ontario.',
    href: 'https://hiv411.ca/'
  },
  {
    id: 'ohtn',
    name: 'Ontario HIV Treatment Network',
    description: 'Research, resources, and service navigation for HIV care in Ontario.',
    href: 'https://www.ohtn.on.ca/'
  }
];

export default function LivingWithHIVPage() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const targetId = location.hash.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen flex flex-col relative z-10" data-testid="living-with-hiv-page">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="living-with-hiv-back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-3xl emoji-bounce">💙</span>
              <h1 className="text-2xl font-bold text-gradient" data-testid="living-with-hiv-title">
                Living with HIV
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        <section className="mb-8 p-6 rounded-[2rem] bg-gradient-to-br from-sky-500/80 via-cyan-500/70 to-indigo-600/80 border border-sky-300/20 text-white" data-testid="living-with-hiv-hero">
          <div className="text-5xl mb-4">🌈</div>
          <h2 className="text-3xl font-semibold mb-3">Care, pleasure, and community can exist together.</h2>
          <p className="text-white/90 leading-relaxed text-sm sm:text-base">
            This page is for real-life support: meds, U=U, safer partying, emotional care, and Canadian resources that do not talk down to you.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="px-3 py-2 rounded-full bg-white/12 text-sm font-medium" data-testid="living-with-hiv-chip-u-equals-u">U=U</span>
            <span className="px-3 py-2 rounded-full bg-white/12 text-sm font-medium" data-testid="living-with-hiv-chip-treatment">Treatment works</span>
            <span className="px-3 py-2 rounded-full bg-white/12 text-sm font-medium" data-testid="living-with-hiv-chip-peer-support">Peer support matters</span>
          </div>
        </section>

        <section className="space-y-4 mb-8" data-testid="living-with-hiv-sections">
          {sections.map((section) => (
            <article
              key={section.id}
              id={section.id}
              data-testid={`living-with-hiv-section-${section.id}`}
              className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-6"
            >
              <div className="flex items-start gap-4">
                <div className="text-4xl">{section.emoji}</div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-[rgb(var(--text-primary))] mb-2">{section.title}</h3>
                  <p className="text-sm sm:text-base text-[rgb(var(--text-secondary))] leading-relaxed mb-4">
                    {section.description}
                  </p>
                  <ul className="space-y-2">
                    {section.bullets.map((bullet, index) => (
                      <li
                        key={`${section.id}-${index}`}
                        className="rounded-2xl bg-white/5 border border-white/5 px-4 py-3 text-sm text-[rgb(var(--text-secondary))]"
                      >
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </section>

        <section className="mb-8 rounded-[2rem] border border-[rgb(var(--primary))]/25 bg-gradient-to-r from-[rgb(var(--primary))]/12 to-[rgb(var(--secondary))]/10 p-6" data-testid="living-with-hiv-checklist">
          <h3 className="text-xl font-semibold text-[rgb(var(--text-primary))] mb-4">Tonight / this week care checklist</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              'Take meds or set your next reminder.',
              'Pack water, lube, barriers, and anything you need for safer use.',
              'Plan sleep, food, and recovery time after the party.',
              'Save one clinic or peer-support resource before you need it.'
            ].map((item, index) => (
              <div key={index} data-testid={`living-with-hiv-checklist-item-${index}`} className="rounded-2xl bg-black/10 px-4 py-3 text-sm text-[rgb(var(--text-secondary))]">
                {item}
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-3" data-testid="living-with-hiv-resources">
          <h3 className="text-sm font-medium uppercase tracking-wider text-[rgb(var(--text-secondary))]">Canadian support links</h3>
          {resources.map((resource) => (
            <a
              key={resource.id}
              href={resource.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`living-with-hiv-resource-${resource.id}`}
              className="block rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5 transition-colors hover:border-[rgb(var(--primary))]/40 hover:bg-[rgb(var(--surface-elevated))]"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-lg font-semibold text-[rgb(var(--text-primary))]">{resource.name}</h4>
                  <p className="mt-2 text-sm text-[rgb(var(--text-secondary))] leading-relaxed">{resource.description}</p>
                </div>
                <span className="text-sm font-medium text-[rgb(var(--primary))]">Open ↗</span>
              </div>
            </a>
          ))}
        </section>
      </main>
    </div>
  );
}